import { useMemo, useState } from 'react';
import AnimatedNumber from '../components/AnimatedNumber.jsx';
import Icon from '../components/Icon.jsx';
import { populationZones } from '../data.js';
import './PopulationSlide.css';

const DISPLAY_TOTAL = 100000;

const zoneMeta = {
  'Al Samha': { corridor: 'West corridor', position: { x: 16, y: 29 } },
  'Al Shahama': { corridor: 'North-east corridor', position: { x: 76, y: 27 } },
  'Al Falah': { corridor: 'South-east corridor', position: { x: 76, y: 65 } },
  'Al Bahia': { corridor: 'South-west corridor', position: { x: 17, y: 66 } },
  'Al Rahba': { corridor: 'Primary service hub', position: { x: 47, y: 47 } },
};

function formatValue(value) {
  return value.toLocaleString('en-GB');
}

function routePath(item, hub) {
  const itemPosition = zoneMeta[item.name].position;
  const hubPosition = zoneMeta[hub.name].position;
  const midX = (hubPosition.x + itemPosition.x) / 2;
  const midY = (hubPosition.y + itemPosition.y) / 2;
  const curve = itemPosition.y < hubPosition.y ? -8 : 8;

  return `M ${hubPosition.x} ${hubPosition.y} C ${midX} ${midY + curve}, ${midX} ${itemPosition.y - curve}, ${itemPosition.x} ${itemPosition.y}`;
}

export default function PopulationSlide({ active }) {
  const [selected, setSelected] = useState(2);
  const selectedZone = populationZones[selected];
  const hub = populationZones.find((item) => item.hub) || populationZones[1];
  const communities = populationZones.filter((item) => !item.hub);
  const maxValue = Math.max(...populationZones.map((item) => item.value));

  const rankedCommunities = useMemo(
    () => populationZones.filter((item) => !item.hub).sort((a, b) => b.value - a.value),
    [],
  );

  const selectedRank = selectedZone.hub
    ? null
    : rankedCommunities.findIndex((item) => item.name === selectedZone.name) + 1;
  const selectedShare = Math.round((selectedZone.value / DISPLAY_TOTAL) * 100);

  const selectZone = (name) => {
    const zoneIndex = populationZones.findIndex((item) => item.name === name);
    if (zoneIndex >= 0) setSelected(zoneIndex);
  };

  return (
    <section className="slide-content population-slide catchment-v4">
      <header className="catchment-v4-heading reveal-stack">
        <div className="catchment-heading-copy">
          <div className="eyebrow dark">
            <Icon name="network" />
            Catchment population
          </div>
          <h2>
            Population <em>Orbit</em>
          </h2>
          <p>Four connected communities surrounding Al Rahba as the primary service hub.</p>
        </div>

        <div className="catchment-total-readout">
          <small>Total catchment estimate</small>
          <AnimatedNumber value={DISPLAY_TOTAL} prefix="~" active={active} />
          <i><b style={{ width: '100%' }} /></i>
        </div>
      </header>

      <article className="catchment-network-stage">
        <div className="catchment-stage-grid" aria-hidden="true" />
        <div className="catchment-stage-glow glow-a" aria-hidden="true" />
        <div className="catchment-stage-glow glow-b" aria-hidden="true" />
        <div className="catchment-scene-number" aria-hidden="true">07</div>

        <div className="catchment-stage-header">
          <div>
            <span>Interactive population network</span>
            <b>Hover or select a community</b>
          </div>
          <div className="catchment-live-readout">
            <span>Selected</span>
            <strong>{selectedZone.name}</strong>
            <b>{selectedShare}%</b>
          </div>
        </div>

        <div className="catchment-orbit orbit-one" aria-hidden="true" />
        <div className="catchment-orbit orbit-two" aria-hidden="true" />
        <div className="catchment-orbit orbit-three" aria-hidden="true" />

        <svg className="catchment-v4-routes" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="catchmentRoute" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#8fb6d8" stopOpacity=".26" />
              <stop offset="1" stopColor="#46b4e8" stopOpacity=".58" />
            </linearGradient>
            <linearGradient id="catchmentActiveRoute" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#d2c6b5" />
              <stop offset=".38" stopColor="#46b4e8" />
              <stop offset="1" stopColor="#ffffff" />
            </linearGradient>
          </defs>
          {communities.map((item) => (
            <path
              key={item.name}
              className={item.name === selectedZone.name ? 'is-active' : ''}
              d={routePath(item, hub)}
            />
          ))}
        </svg>

        <button
          className={`catchment-v4-hub ${selectedZone.hub ? 'is-active' : ''}`}
          style={{
            left: `${zoneMeta[hub.name].position.x}%`,
            top: `${zoneMeta[hub.name].position.y}%`,
          }}
          onMouseEnter={() => selectZone(hub.name)}
          onFocus={() => selectZone(hub.name)}
          onClick={() => selectZone(hub.name)}
          type="button"
        >
          <i className="hub-radar radar-a" aria-hidden="true" />
          <i className="hub-radar radar-b" aria-hidden="true" />
          <span>Primary hub</span>
          <strong>{hub.name}</strong>
          <b>{formatValue(hub.value)}</b>
          <small>Service population</small>
        </button>

        {communities.map((item) => {
          const originalIndex = populationZones.findIndex((zone) => zone.name === item.name);
          const rank = rankedCommunities.findIndex((zone) => zone.name === item.name) + 1;
          const share = Math.round((item.value / DISPLAY_TOTAL) * 100);
          const position = zoneMeta[item.name].position;

          return (
            <button
              className={`catchment-v4-node ${selected === originalIndex ? 'is-active' : ''}`}
              key={item.name}
              style={{
                left: `${position.x}%`,
                top: `${position.y}%`,
                '--node-fill': `${Math.max(18, (item.value / maxValue) * 100)}%`,
              }}
              onMouseEnter={() => setSelected(originalIndex)}
              onFocus={() => setSelected(originalIndex)}
              onClick={() => setSelected(originalIndex)}
              type="button"
            >
              <span className="catchment-node-rank">#{rank}</span>
              <span className="catchment-node-copy">
                <small>{zoneMeta[item.name].corridor}</small>
                <strong>{item.name}</strong>
              </span>
              <b>{formatValue(item.value)}</b>
              <i className="catchment-node-meter"><em /></i>
              <span className="catchment-node-share">{share}%</span>
              {selected === originalIndex && <span className="catchment-selected-tag">Selected</span>}
            </button>
          );
        })}

        <div className="catchment-ranking-dock">
          <div className="catchment-dock-label">
            <small>Population ranking</small>
            <b>Largest to smallest</b>
          </div>

          <div className="catchment-rank-items">
            {rankedCommunities.map((item, index) => {
              const isSelected = item.name === selectedZone.name;
              return (
                <button
                  className={isSelected ? 'is-active' : ''}
                  key={item.name}
                  onMouseEnter={() => selectZone(item.name)}
                  onFocus={() => selectZone(item.name)}
                  onClick={() => selectZone(item.name)}
                  type="button"
                >
                  <span>0{index + 1}</span>
                  <b>{item.name}</b>
                  <strong>{formatValue(item.value)}</strong>
                  <i><em style={{ width: `${Math.max(16, (item.value / maxValue) * 100)}%` }} /></i>
                </button>
              );
            })}
          </div>

          <div className="catchment-dock-focus">
            <span>{selectedZone.hub ? 'Mode' : 'Rank'}</span>
            <b>{selectedZone.hub ? 'Hub' : `#${selectedRank}`}</b>
          </div>
        </div>
      </article>
    </section>
  );
}
